import { LegalPageLayout } from "../components/LegalPageLayout";

export function PrivacyPolicy() {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      subtitle="Dough Mobile Application"
      lastUpdated="February 2025"
    >
      <section>
        <h2>1. Introduction</h2>
        <p>
          BetterTime Co. ("we," "us," or "our") operates the Dough mobile application (the "App").
          This Privacy Policy explains how we collect, use, disclose, and safeguard your information
          when you use our App. Please read this Privacy Policy carefully. By using the App, you agree
          to the collection and use of information in accordance with this policy. If you do not agree
          with the terms of this Privacy Policy, please do not access or use the App.
        </p>
        <p>
          We reserve the right to make changes to this Privacy Policy at any time and for any reason.
          We will alert you about any changes by updating the "Last Updated" date of this Privacy Policy.
          You are encouraged to periodically review this Privacy Policy to stay informed of updates.
        </p>
      </section>

      <section>
        <h2>2. Information We Collect</h2>
        <p>
          We collect information that you provide directly to us when you create an account, use our
          services, or communicate with us.
        </p>

        <h3>Account Information</h3>
        <ul>
          <li>Email address (required for account creation)</li>
          <li>Phone number (required for account verification)</li>
          <li>Apple ID or Google account information (if you choose to sign in using these services)</li>
          <li>Nickname (chosen by you for display on leaderboards)</li>
          <li>Avatar selection (chosen from pre-generated options)</li>
        </ul>

        <h3>Usage Information</h3>
        <ul>
          <li>Learning module completion and progress data</li>
          <li>Simulation game participation and scores</li>
          <li>AI coach conversation history</li>
          <li>Leaderboard rankings and statistics</li>
        </ul>

        <h3>Information Stored Locally on Your Device</h3>
        <ul>
          <li>Budgeting tool data (stored only on your device, not transmitted to our servers)</li>
          <li>App preferences and settings</li>
        </ul>

        <h3>Information We Do NOT Collect</h3>
        <ul>
          <li>Bank account information or credentials</li>
          <li>Credit card or payment information</li>
          <li>Social Security numbers or government IDs</li>
          <li>Actual financial transaction data</li>
          <li>Location data</li>
          <li>Contact lists or address books</li>
        </ul>
      </section>

      <section>
        <h2>3. How We Use Your Information</h2>
        <p>We use the information we collect for the following purposes:</p>

        <h3>Account Management</h3>
        <ul>
          <li>To create and manage your account</li>
          <li>To verify your identity through email and phone verification</li>
          <li>To prevent fraud and enforce our one-account-per-user policy</li>
        </ul>

        <h3>Service Delivery</h3>
        <ul>
          <li>To provide the financial wellness educational content</li>
          <li>To track your progress through learning modules</li>
          <li>To operate the weekly simulation game and maintain leaderboards</li>
          <li>To provide AI-powered financial wellness coaching</li>
          <li>To deliver rewards based on simulation game performance</li>
        </ul>

        <h3>Communication</h3>
        <ul>
          <li>To send you verification emails and SMS codes</li>
          <li>To notify you about changes to our services or policies</li>
          <li>To respond to your inquiries and support requests</li>
        </ul>

        <h3>Improvement and Analytics</h3>
        <ul>
          <li>To analyze usage patterns and improve our services</li>
          <li>To identify and fix technical issues</li>
          <li>To develop new features and content</li>
        </ul>
      </section>

      <section>
        <h2>4. Information Sharing and Disclosure</h2>
        <p>
          We do not sell, trade, or rent your personal information to third parties. We may share your
          information only in the following limited circumstances:
        </p>

        <h3>With Your Consent</h3>
        <ul>
          <li>When you explicitly authorize us to share information</li>
        </ul>

        <h3>Service Providers</h3>
        <ul>
          <li>
            We use Google Cloud Platform (including Firebase) to host and operate our services.
            Your data is processed in accordance with Google's privacy and security standards.
          </li>
          <li>
            We use AI services through Google Cloud Platform to power the AI coach feature.
            Conversation context is processed to generate responses but is not used to train AI models.
          </li>
        </ul>

        <h3>Public Leaderboards</h3>
        <ul>
          <li>
            Your nickname, avatar, and simulation game ranking are visible to other users on the
            leaderboard. No other personal information is displayed.
          </li>
        </ul>

        <h3>Legal Requirements</h3>
        <ul>
          <li>
            We may disclose your information if required to do so by law or in response to valid
            requests by public authorities.
          </li>
        </ul>

        <h3>Business Transfers</h3>
        <ul>
          <li>
            In the event of a merger, acquisition, or sale of assets, your information may be
            transferred as part of that transaction.
          </li>
        </ul>
      </section>

      <section>
        <h2>5. Data Security</h2>
        <p>
          We implement appropriate technical and organizational measures to protect your personal
          information against unauthorized access, alteration, disclosure, or destruction.
        </p>

        <h3>Security Measures</h3>
        <ul>
          <li>All data transmitted between the App and our servers is encrypted using TLS (Transport Layer Security)</li>
          <li>Data stored in our databases is encrypted at rest using Google-managed encryption</li>
          <li>Authentication tokens are securely managed through Firebase Authentication</li>
          <li>Access to user data is restricted to authorized personnel only</li>
        </ul>

        <h3>Your Responsibilities</h3>
        <ul>
          <li>Keep your account credentials confidential</li>
          <li>Use a strong, unique password if using email/password authentication</li>
          <li>Sign out of your account when using shared devices</li>
          <li>Notify us immediately if you suspect unauthorized access to your account</li>
        </ul>

        <h3>Local Data Protection</h3>
        <ul>
          <li>
            Budgeting data stored locally on your device is protected by your device's native security
            features (iOS Data Protection, Android encryption)
          </li>
          <li>We recommend enabling a device passcode or biometric lock for additional protection</li>
        </ul>
      </section>

      <section>
        <h2>6. Data Retention</h2>
        <p>
          We retain your personal information for as long as your account is active or as needed to
          provide you services.
        </p>

        <h3>Retention Periods</h3>
        <ul>
          <li>Account information: Retained while your account is active</li>
          <li>Learning progress: Retained while your account is active</li>
          <li>Simulation game history: Retained while your account is active</li>
          <li>AI coach conversations: Retained while your account is active</li>
          <li>Leaderboard data: Current week's rankings displayed; historical rankings archived</li>
        </ul>

        <h3>After Account Deletion</h3>
        <ul>
          <li>Most personal data is deleted within 30 days of account deletion</li>
          <li>Email addresses are retained in a suppression list to prevent account re-creation (for fraud prevention)</li>
          <li>Anonymized, aggregated data may be retained for analytics purposes</li>
          <li>Backup copies may persist for up to 90 days before being purged</li>
        </ul>
      </section>

      <section>
        <h2>7. Your Rights and Choices</h2>
        <p>You have certain rights regarding your personal information:</p>

        <h3>Access and Portability</h3>
        <ul>
          <li>You can access your account information through the App</li>
          <li>You can request a copy of your personal data by contacting us</li>
        </ul>

        <h3>Correction</h3>
        <ul>
          <li>You can update your nickname and avatar at any time through the App</li>
          <li>Contact us to correct other account information</li>
        </ul>

        <h3>Deletion</h3>
        <ul>
          <li>You can request deletion of your account through the App</li>
          <li>Account deletion will remove your personal data from our active systems</li>
          <li>Note: Email addresses from deleted accounts are retained to prevent re-registration (this supports fraud prevention and leaderboard integrity)</li>
        </ul>

        <h3>Opt-Out</h3>
        <ul>
          <li>You can opt out of promotional communications by following the unsubscribe instructions in those messages</li>
        </ul>

        <h3>Do Not Track</h3>
        <ul>
          <li>The App does not currently respond to "Do Not Track" signals</li>
        </ul>

        <h3>Contact Us</h3>
        <p>
          To exercise any of these rights, contact us at{" "}
          <a href="mailto:help@betteryou.ai">help@betteryou.ai</a>.
        </p>
      </section>

      <section>
        <h2>8. Children's Privacy</h2>
        <p>
          The App is intended for users aged 13 and older. We comply with the Children's Online
          Privacy Protection Act (COPPA) and similar laws.
        </p>

        <h3>Age Requirements</h3>
        <ul>
          <li>Users must be at least 13 years old to create an account</li>
          <li>Users between 13 and 17 years old must have parental or guardian consent to use the App</li>
        </ul>

        <h3>Parental Consent</h3>
        <ul>
          <li>
            By allowing a minor to use the App, parents or guardians consent to the collection and use
            of their child's information as described in this Privacy Policy
          </li>
          <li>
            Parents or guardians may review their child's information, request deletion, or revoke
            consent by contacting us
          </li>
        </ul>

        <h3>Information from Children</h3>
        <ul>
          <li>We do not knowingly collect personal information from children under 13</li>
          <li>
            If we learn that we have collected personal information from a child under 13 without
            parental consent, we will delete that information promptly
          </li>
        </ul>

        <h3>Parental Rights</h3>
        <p>
          Parents may contact us at <a href="mailto:help@betteryou.ai">help@betteryou.ai</a> to:
        </p>
        <ul>
          <li>Review their child's personal information</li>
          <li>Request deletion of their child's account and data</li>
          <li>Revoke consent for further collection of their child's information</li>
        </ul>
      </section>

      <section>
        <h2>9. California Privacy Rights</h2>
        <p>
          If you are a California resident, you have additional rights under the California Consumer
          Privacy Act (CCPA):
        </p>

        <h3>Right to Know</h3>
        <ul>
          <li>
            You have the right to request information about the categories and specific pieces of
            personal information we have collected about you
          </li>
        </ul>

        <h3>Right to Delete</h3>
        <ul>
          <li>You have the right to request deletion of your personal information, subject to certain exceptions</li>
        </ul>

        <h3>Right to Non-Discrimination</h3>
        <ul>
          <li>We will not discriminate against you for exercising your privacy rights</li>
        </ul>

        <h3>Categories of Information Collected</h3>
        <ul>
          <li>Identifiers (email, phone number, account IDs)</li>
          <li>Internet or network activity (usage data, log data)</li>
          <li>Inferences drawn from the above (preferences, characteristics)</li>
        </ul>

        <p>We do not sell personal information as defined by the CCPA.</p>
        <p>
          To exercise your California privacy rights, contact us at{" "}
          <a href="mailto:help@betteryou.ai">help@betteryou.ai</a> with the subject line
          "California Privacy Request."
        </p>
      </section>

      <section>
        <h2>10. International Users</h2>
        <p>
          The App is operated from the United States. If you are accessing the App from outside the
          United States, please be aware that your information may be transferred to, stored, and
          processed in the United States.
        </p>
        <p>
          By using the App, you consent to the transfer of your information to the United States and
          the processing of your information in accordance with this Privacy Policy.
        </p>
        <p>We comply with applicable data protection laws, including:</p>
        <ul>
          <li>General Data Protection Regulation (GDPR) for users in the European Economic Area</li>
          <li>Other applicable local privacy laws</li>
        </ul>
        <p>
          If you are located in the European Economic Area, you have additional rights including the
          right to lodge a complaint with your local data protection authority.
        </p>
      </section>

      <section>
        <h2>11. Third-Party Services</h2>
        <p>The App uses the following third-party services:</p>

        <h3>Google Cloud Platform / Firebase</h3>
        <ul>
          <li>Provides authentication, database, hosting, and analytics services</li>
          <li>
            Privacy Policy:{" "}
            <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">
              https://firebase.google.com/support/privacy
            </a>
          </li>
        </ul>

        <h3>Google AI Services</h3>
        <ul>
          <li>Powers the AI coach feature</li>
          <li>Conversation data is processed to generate responses</li>
          <li>
            Privacy Policy:{" "}
            <a href="https://cloud.google.com/terms/cloud-privacy-notice" target="_blank" rel="noopener noreferrer">
              https://cloud.google.com/terms/cloud-privacy-notice
            </a>
          </li>
        </ul>

        <h3>Apple Sign In (if used)</h3>
        <ul>
          <li>Provides authentication services</li>
          <li>
            Privacy Policy:{" "}
            <a href="https://www.apple.com/legal/privacy" target="_blank" rel="noopener noreferrer">
              https://www.apple.com/legal/privacy
            </a>
          </li>
        </ul>

        <h3>Google Sign In (if used)</h3>
        <ul>
          <li>Provides authentication services</li>
          <li>
            Privacy Policy:{" "}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
              https://policies.google.com/privacy
            </a>
          </li>
        </ul>

        <p>We encourage you to review the privacy policies of these third-party services.</p>
      </section>

      <section>
        <h2>12. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of any changes by:
        </p>
        <ul>
          <li>Posting the new Privacy Policy in the App</li>
          <li>Updating the "Last Updated" date at the top of this Privacy Policy</li>
          <li>Sending you an email notification for material changes (if we have your email address)</li>
        </ul>
        <p>
          You are advised to review this Privacy Policy periodically for any changes. Changes to this
          Privacy Policy are effective when they are posted.
        </p>
        <p>
          Your continued use of the App after we post changes constitutes your acceptance of those changes.
        </p>
      </section>

      <section>
        <h2>13. Contact Us</h2>
        <p>
          If you have questions or concerns about this Privacy Policy or our privacy practices,
          please contact us:
        </p>
        <p>
          BetterTime Co.<br />
          Email: <a href="mailto:help@betteryou.ai">help@betteryou.ai</a>
        </p>
        <p>We will respond to your inquiry within 30 days.</p>
      </section>
    </LegalPageLayout>
  );
}
